import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { INR, titleCase } from "@/lib/format";
import { FormDialog } from "@/components/EntityForm";
import { PageHeader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { KycPanel, GstPanel, BankPanel, DocsPanel, Card, run } from "@/components/agent/AgentPanels";
import { PayoutsPanel, WithdrawalsPanel } from "@/components/agent/PayoutPanels";
import { AGENT_FIELDS, prepAgent } from "@/pages/Agents";
import { BadgeCheck, ArrowLeft, Pencil, Loader2 } from "lucide-react";

const Stat = ({ label, value, testId }) => (
  <div className="rounded-xl border border-border bg-card p-4">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="text-lg font-bold mt-1" data-testid={testId}>{value}</div>
  </div>
);

export default function AgentDetail() {
  const { id } = useParams();
  const { hasPerm } = useAuth();
  const [d, setD] = useState(null);
  const load = useCallback(async () => {
    const [p, docs, pay, wd] = await Promise.all([
      api.get(`/agents/${id}/profile`), api.get(`/agents/${id}/documents`),
      api.get(`/agents/${id}/payouts`), api.get(`/agents/${id}/withdrawals`),
    ]);
    setD({ profile: p.data, docs: docs.data, payouts: pay.data, wd: wd.data });
  }, [id]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Loader2 className="h-6 w-6 animate-spin mx-auto mt-20 text-muted-foreground" />;
  const { profile, docs, payouts, wd } = d;
  const a = profile.agent;
  const lines = (a.business_lines || []).length > 1 ? "both" : (a.business_lines || ["insurance"])[0];

  const toggleOverride = (v) => run(() => api.put(`/agents/${id}`, { payout_override: v }), v ? "Payout override ON" : "Payout override OFF").then(load);

  return (
    <div>
      <Link to="/agents" className="text-sm text-muted-foreground inline-flex items-center gap-1 mb-3" data-testid="agent-back-link"><ArrowLeft className="h-4 w-4" />All agents</Link>
      <PageHeader title={a.name} subtitle={`${a.agent_code} · ${titleCase(a.agent_type)} · ${(a.business_lines || []).map(titleCase).join(" + ")} · ${a.city || ""}`} icon={BadgeCheck}
        actions={hasPerm("agents:edit") && (
          <FormDialog title="Edit Agent" fields={AGENT_FIELDS} initial={{ ...a, pan: a.pan || "", lines }} submitLabel="Update"
            onSubmit={async (v) => { const b = prepAgent(v); if (!a.can_view_sensitive || !b.pan) delete b.pan; await api.put(`/agents/${id}`, b); load(); }}
            trigger={<Button variant="outline" data-testid="agent-edit-btn"><Pencil className="h-4 w-4 mr-1.5" />Edit Profile</Button>} />
        )} />

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-5">
        <Stat label="Insurance payouts (net)" value={INR(profile.insurance_payouts)} testId="stat-ins-payouts" />
        <Stat label="Loan payouts (net)" value={INR(profile.loan_payouts)} testId="stat-loan-payouts" />
        <Stat label="TDS deducted" value={INR(profile.tds_total)} testId="stat-tds" />
        <Stat label="GST on payouts" value={INR(profile.gst_total)} testId="stat-gst" />
        <Stat label="Available balance" value={INR(profile.available_balance)} testId="stat-available" />
        <Stat label="Payout eligible" value={profile.payout_eligible ? "Yes" : "Blocked"} testId="stat-eligible" />
      </div>

      {profile.pending_actions.length > 0 && (
        <div className="mb-5 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-4 text-sm" data-testid="agent-pending-actions">
          <div className="font-semibold mb-1">Pending actions</div>
          <ul className="list-disc pl-5 space-y-0.5">{profile.pending_actions.map((x) => <li key={x}>{x}</li>)}</ul>
        </div>
      )}

      <Tabs defaultValue="kyc">
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="kyc" data-testid="agent-tab-kyc">KYC · GST · Bank</TabsTrigger>
          <TabsTrigger value="docs" data-testid="agent-tab-docs">Documents ({docs.length})</TabsTrigger>
          <TabsTrigger value="payouts" data-testid="agent-tab-payouts">Payouts ({payouts.length})</TabsTrigger>
          <TabsTrigger value="withdrawals" data-testid="agent-tab-withdrawals">Withdrawals</TabsTrigger>
        </TabsList>
        <TabsContent value="kyc" className="grid lg:grid-cols-3 gap-4">
          <KycPanel agent={a} reload={load} />
          <GstPanel agent={a} reload={load} />
          <BankPanel agent={a} reload={load} />
          {hasPerm("*") && (
            <Card title="Admin Payout Override">
              <div className="flex items-center justify-between gap-3 text-sm">
                <span className="text-muted-foreground">Allow payouts even if KYC or bank is not verified</span>
                <Switch checked={!!a.payout_override} onCheckedChange={toggleOverride} data-testid="payout-override-switch" />
              </div>
            </Card>
          )}
        </TabsContent>
        <TabsContent value="docs"><DocsPanel agent={a} docs={docs} profile={profile} reload={load} /></TabsContent>
        <TabsContent value="payouts"><PayoutsPanel agent={a} payouts={payouts} profile={profile} reload={load} /></TabsContent>
        <TabsContent value="withdrawals"><WithdrawalsPanel agent={a} wd={wd} reload={load} /></TabsContent>
      </Tabs>
    </div>
  );
}
